import { beginOperation, finishOperation, operationSlot, readOperation, saveOperation, clearOperation, isDefinitive } from './operations';

const pendingCommands = new Map();
const pendingSessionActions = new Map();

// Translate only known server-authored display messages. Keep API identifiers,
// diagnostic codes and configured entity names intact.
const apiDisplayMessages = new Map([
  ['Reservation not found', 'Tool usage record not found'],
  ['Cannot create a reservation for another user', 'Cannot record tool usage for another user'],
  ['Not an admin for any lab this user is associated with', 'Not an admin for any space this user is associated with'],
]);

//api hook prototype
const apiHook = {
  setURL(URL) {
    this.baseURL = URL;
  },

  // Add method to set API key
  setApiKey(key) {
    this.apiKey = key;
    if (key) {
      localStorage.setItem('pinpoint_kiosk_key', key);
    } else {
      localStorage.removeItem('pinpoint_kiosk_key');
    }
  },

  /// V2 API call helper — Bearer auth, JSON request bodies, no .data unwrap.
  /// - path: Path relative to baseURL (e.g. '/spaces/')
  /// - method: HTTP method
  /// - body: JSON body for mutations (POST/PUT/PATCH); null for GET
  /// - params: URL query params (always added to the URL)
  /// - accept: Response type
  apiCallV2(path, method, body = null, params = {}, accept = 'application/json') {
    const apiKey = this.apiKey || localStorage.getItem('pinpoint_kiosk_key');
    const v2Base = this.baseURL.replace(/\/api\/v\d+$/, '/api/v2');
    const fullURL = new URL(v2Base + path);
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
    );
    if (Object.keys(cleanParams).length > 0) {
      fullURL.search = new URLSearchParams(cleanParams).toString();
    }
    const headers = {
      Accept: accept,
      'Content-Type': 'application/json',
      // Development setupProxy validates and strips this browser-origin hint.
      // Production ignores it; backend trust remains with the proxy headers.
      'X-Pinpoint-Origin': new URL(window.location.href).origin,
    };
    if (apiKey) {
      headers['Authorization'] = `Bearer ${apiKey}`;
    }
    const mutates = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) || path === '/badges/oauth';
    const operation = mutates ? beginOperation(apiKey || 'session', [path, method, body, cleanParams]) : null;
    if (operation && pendingCommands.has(operation.slot)) return pendingCommands.get(operation.slot);
    if (operation) headers['Idempotency-Key'] = operation.key;
    const options = { method, credentials: 'include', headers };
    if (body !== null && method !== 'GET') {
      options.body = JSON.stringify(body);
    }
    const pending = fetch(fullURL, options)
      .then((response) => {
        if (accept === 'text/csv' && response.ok) {
          if (!(response.headers.get('content-type') || '').includes('text/csv')) {
            return { status: 502, message: 'The server did not return a CSV file. Please try again.' };
          }
          return response.blob().then((blob) => {
            const content = response.headers.get('content-disposition') || '';
            const match = content.match(/filename="([^"]+)"|filename=([^;]+)/i);
            const filename = (match?.[1] || match?.[2] || 'download.csv').trim().replace(/[/\\]/g, '_');
            const element = document.createElement('a');
            const objectURL = URL.createObjectURL(blob);
            element.href = objectURL;
            element.download = filename;
            document.body.appendChild(element);
            try {
              element.click();
            } finally {
              element.remove();
              setTimeout(() => URL.revokeObjectURL(objectURL), 0);
            }
            return { status: response.status, filename };
          });
        }
        if (response.status === 204) {
          return { status: response.status };
        }
        return response
          .json()
          .then((json) =>
            Array.isArray(json)
              ? { data: json, status: response.status }
              : {
                  ...json,
                  ...(apiDisplayMessages.has(json?.message)
                    ? { message: apiDisplayMessages.get(json.message) }
                    : {}),
                  status: response.status,
                }
          )
          .catch(() => ({
            status: response.status,
            error: 'parse_error',
            message: 'Unexpected response from server',
          }));
      })
      .catch(() => ({
        status: 0,
        error: 'network_error',
        message: 'Unable to reach Pinpoint servers',
      }))
      .then(result => finishOperation(operation, result))
      .finally(() => { if (operation) pendingCommands.delete(operation.slot); });
    if (operation) pendingCommands.set(operation.slot, pending);
    return pending;
  },

  // Bind a multi-request action before sending its mutation. An uncertain
  // retry must keep acting on that visit even if a newer visit has started.
  async withActiveSession(intent, userId, spaceId, action) {
    const scope = this.apiKey || localStorage.getItem('pinpoint_kiosk_key') || 'session';
    const slot = operationSlot(scope, ['active-session', intent, userId, spaceId]);
    if (pendingSessionActions.has(slot)) return pendingSessionActions.get(slot);
    const pending = (async () => {
      let bound = readOperation(slot);
      if (!bound) {
        const active = await this.apiCallV2('/sessions/active', 'GET', null, { user_id: userId, space_id: spaceId });
        if (active.status !== 200 || active.error) return active;
        if (!active.data?.length) return { status: 404, error: 'No active session found' };
        bound = saveOperation(slot, { sessionId: active.data[0].id });
      }
      const response = await action(bound.sessionId);
      if (isDefinitive(response)) clearOperation(slot);
      return response;
    })();
    pendingSessionActions.set(slot, pending);
    try {
      return await pending;
    } finally {
      pendingSessionActions.delete(slot);
    }
  },

  /// Returns badges the user has earned that Pinpoint recognizes, with tool names
  /// - UID: The University ID of the user to query
  getUserEarnedBadges(UID) {
    return this.apiCallV2(`/users/${UID}/earned_badges`, 'GET');
  },

  getVersion() {
    return fetch('/api/v2/', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        const version = (data.version || '').replace(/^pinpoint@/, '') || null;
        const envNames = { prod: 'production', dev: 'development', test: 'testing' };
        const environment = envNames[data.environment] || data.environment || null;
        return { version, environment };
      })
      .catch(() => ({ version: null, environment: null }));
  },

  // V2: open an SSE connection to receive live session events for a space.
  // Returns the EventSource — caller must call .close() on unmount.
  subscribeToSpaceEvents(spaceId, onEvent, onError) {
    const v2Base = this.baseURL.replace(/\/api\/v\d+$/, '/api/v2');
    const es = new EventSource(`${v2Base}/spaces/${spaceId}/events`);
    const seenEvents = new Set();
    es.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data);
        const id = event.event_id || e.lastEventId;
        if (id && seenEvents.has(id)) return;
        if (id) {
          seenEvents.add(id);
          if (seenEvents.size > 500) seenEvents.delete(seenEvents.values().next().value);
        }
        onEvent(event);
      } catch (_) {}
    };
    if (onError) es.onerror = onError;
    return es;
  },
};

// The page's origin owns both the UI and /api routes. Development proxies and
// deployment ingress route API traffic without changing the browser's host.
const Backend = Object.create(apiHook);
Backend.setURL(new URL('/api/v2', window.location.href).toString());

// ── V2 implementations ───────────────────────────────────────────────────────
// Block-scoped so the field-mapping helpers stay file-private.
{

  // ── Field mapping helpers ─────────────────────────────────────────────────

  const mapUser = (u) => ({
    UID: u.uid ?? u.id,
    id: u.id,
    uid: u.uid ?? u.id,
    firstName: u.first_name,
    lastName: u.last_name,
    email: u.email,
    swipeNum: u.swipe_number,
    directoryID: u.directory_id,
    isSuperAdmin: u.is_super_admin,
    consecutive_no_swipeout: u.consecutive_no_swipeout,
  });

  const mapSpace = (s) => ({
    id: s.id,
    name: s.name,
    badgrIntegration: s.badge_integration,
    skipCheckInPurpose: Boolean(s.skip_check_in_purpose),
    skipToolSelection: Boolean(s.skip_tool_selection),
    requireToolSelection: s.require_tool_selection !== false,
    deleted: s.deleted,
  });

  const mapToolInstance = (m) => ({
    id: m.id,
    machineID: m.id,
    name: m.name,
    machineName: m.name,
    machineType: m.tool_id,   // integer tool type ID (components use for lookup)
    tool_id: m.tool_id,
    categoryID: m.category_id,
    category_id: m.category_id,
    lab: m.space_id,
    space_id: m.space_id,
    labID: m.space_id,
    sublabID: m.subspace_id,
    status: m.status,
    deleted: m.deleted,
  });

  const mapTool = (t) => ({
    id: t.id,
    name: t.name,
    machineType_ID: t.id,
    machineType: t.name,     // string name alias for backward compat
    isDeleted: t.is_deleted,
    is_deleted: t.is_deleted,
  });

  const mapToolCategory = (c) => ({
    id: c.id,
    categoryID: c.id,
    name: c.name,
    categoryName: c.name,
    labID: c.space_id,
    space_id: c.space_id,
    isDeleted: c.is_deleted,
  });

  const mapSpacePerm = (p) => ({
    id: p.id,
    accessID: p.id,
    user: p.user_id,
    user_id: p.user_id,
    lab: p.space_id,
    space_id: p.space_id,
    is_admin: p.is_admin,
    visit_count: p.visit_count,
    directoryID: p.directory_id,
    firstName: p.first_name,
    lastName: p.last_name,
  });

  const mapCustomReason = (r) => ({
    id: r.id,
    custom_reason: r.reason,
    reason: r.reason,
    time_interval: r.time_interval,
    isDeleted: r.is_deleted,
    enable_email_reminder: r.enable_email_reminder,
    enable_time_tracking: r.enable_time_tracking,
  });

  const mapSession = (s) => ({
    id: s.id,
    user_id: s.user_id,
    space_id: s.space_id,
    swipeNum: s.swipe_number,
    startedAt: s.started_at,
    endedAt: s.ended_at,
    autoEndAt: s.auto_end_at,
    status: s.status,
    autoEnded: s.auto_ended,
  });

  // Convert v2 paginated list {data: [...], ...} → {status, items: [...], ...}
  const mapList = (res, mapFn) => {
    if (res.status !== 200 || res.error) return res;
    return {
      status: res.status,
      items: (res.data || []).map(mapFn),
      page: res.page,
      per_page: res.per_page,
      total: res.total,
    };
  };

  // These callers paginate/search locally, so expose one complete collection.
  // Never return a successful partial list: the kiosk also uses these catalogs
  // to decide whether a data-entry step has any available choices.
  const allCatalogPages = async (backend, path, params = {}) => {
    const items = [];
    const seenIds = new Set();
    let total;
    let perPage = Math.min(params.per_page || 200, 200);
    const incomplete = () => ({
      status: 502,
      error: 'incomplete_catalog',
      message: 'The full list could not be loaded. Please try again.',
    });
    for (let page = 1; ; page += 1) {
      const response = await backend.apiCallV2(path, 'GET', null, { ...params, page, per_page: perPage });
      if (response.status !== 200) return response;
      if (response.error || !Array.isArray(response.data) ||
          !Number.isSafeInteger(response.total) || response.total < 0 ||
          response.page !== page || !Number.isSafeInteger(response.per_page) || response.per_page < 1) {
        return incomplete();
      }
      if (page === 1) {
        total = response.total;
        perPage = response.per_page;
      }
      if (response.total !== total || response.per_page !== perPage ||
          response.data.length !== Math.min(perPage, total - items.length)) return incomplete();
      for (const item of response.data) {
        // Repeated records can hide missing records when an offset shifts.
        if (item.id !== undefined) {
          if (seenIds.has(item.id)) return incomplete();
          seenIds.add(item.id);
        }
      }
      items.push(...response.data);
      if (items.length === total) {
        return { status: 200, data: items, total, page: 1, per_page: Math.max(1, total) };
      }
    }
  };

  // Cache: reason_id → space_id, populated by getReasons / getAllReasons.
  // editReason and toggleCustomReasonArchive need this since their v1
  // signatures don't include the space_id.
  Backend._reasonSpaceCache = {};
  const _cacheReasons = (spaceId, items) => {
    items.forEach((r) => { Backend._reasonSpaceCache[r.id] = spaceId; });
  };

  // Resolve a machine type string name → integer tool_id via GET /tools/.
  // Uses Number() (not parseInt) so "3D Printer" → NaN rather than 3.
  // Names are matched trimmed and case-insensitively: V1 resolved names in
  // MySQL, whose default collation ignores case and trailing spaces, so
  // admins are used to that tolerance. An exact match wins over a loose one
  // in case two tool names differ only by case. Returns the integer id, or
  // null if the name doesn't match any tool.
  const _resolveToolId = (backend, machineTypeOrId) => {
    const raw = String(machineTypeOrId ?? '').trim();
    if (raw === '') return Promise.resolve(null);
    const asNum = Number(raw);
    if (Number.isInteger(asNum)) return Promise.resolve(asNum);
    const wanted = raw.toLowerCase();
    return allCatalogPages(backend, '/tools/').then((res) => {
      if (res.status !== 200) return null;
      const tools = res.data || [];
      const match =
        tools.find((t) => t.name === machineTypeOrId) ||
        tools.find((t) => String(t.name ?? '').trim().toLowerCase() === wanted);
      return match ? match.id : null;
    });
  };

  const _toolNotFound = (machineType) => ({
    status: 404,
    error: `No tool named "${machineType}" exists. Check the spelling against the tools list.`,
  });

  // ── Auth ──────────────────────────────────────────────────────────────────

  Backend.getSessionUser = function () {
    return this.apiCallV2('/authentication/profile', 'GET').then((res) => {
      if (res.status === 200 && res.user) return { status: 200, user: res.user };
      return { status: res.status, error: res.error || 'unauthenticated' };
    });
  };

  // Feature flags for the signed-in user (GitLab, evaluated by the backend).
  // Anything but a 200 with a flags object counts as "no flags": all off.
  Backend.getFeatureFlags = function () {
    return this.apiCallV2('/features', 'GET').then((res) => {
      const flags = res.flags;
      if (res.status === 200 && flags && typeof flags === 'object' && !Array.isArray(flags)) {
        return { status: 200, flags };
      }
      return { status: res.status, error: res.error || 'unavailable', flags: {} };
    });
  };

  Backend.casLogin = function () {
    return this.apiCallV2('/authentication/login', 'GET');
  };

  Backend.casLogout = function () {
    return this.apiCallV2('/authentication/logout', 'GET');
  };

  Backend.casLoginCallback = function (ticket, next) {
    return this.apiCallV2('/authentication/login', 'GET', null, { ticket, next });
  };

  Backend.badgrOAuth = function (code, provider = 'parchment') {
    return this.apiCallV2('/badges/oauth', 'GET', null, { code, provider });
  };

  Backend.listBadgeProviders = function (redirect_uri) {
    return this.apiCallV2('/badges/providers', 'GET', null, { redirect_uri });
  };

  // ── Spaces (Labs) ─────────────────────────────────────────────────────────

  Backend.getLabs = function (num) {
    return allCatalogPages(this, '/spaces/', { per_page: num || 100 }).then(
      (res) => mapList(res, mapSpace)
    );
  };

  Backend.getLabById = function ({ id, UID }) {
    return this.apiCallV2(`/spaces/${id}`, 'GET').then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapSpace(res) };
    });
  };

  Backend.newLab = function ({ UID, id, name }) {
    return this.apiCallV2(`/spaces/${id}`, 'PUT', {
      name,
      badge_integration: false,
      deleted: false,
    }).then((res) => {
      if (res.status !== 200 && res.status !== 201) return res;
      return { status: res.status, ...mapSpace(res) };
    });
  };

  Backend.editLabSkipCheckInPurpose = function (id, UID, enabled) {
    return this.apiCallV2(`/spaces/${id}`, 'PATCH', { skip_check_in_purpose: enabled }).then(res =>
      res.status === 200 ? { status: res.status, ...mapSpace(res) } : res
    );
  };

  Backend.editLabRequireToolSelection = function (id, UID, enabled) {
    return this.apiCallV2(`/spaces/${id}`, 'PATCH', { require_tool_selection: enabled }).then(res =>
      res.status === 200 ? { status: res.status, ...mapSpace(res) } : res
    );
  };

  Backend.editLabSkipToolSelection = function (id, UID, enabled) {
    return this.apiCallV2(`/spaces/${id}`, 'PATCH', { skip_tool_selection: enabled }).then(res =>
      res.status === 200 ? { status: res.status, ...mapSpace(res) } : res
    );
  };

  Backend.editLab = function (UID, id, name, badgrIntegration) {
    return this.apiCallV2(`/spaces/${id}`, 'PATCH', {
      name,
      badge_integration: badgrIntegration === true || badgrIntegration === 'True',
    }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapSpace(res) };
    });
  };

  Backend.editLabBadgrIntegration = function (id, UID, badgrIntegration) {
    return this.apiCallV2(`/spaces/${id}`, 'PATCH', {
      badge_integration: badgrIntegration,
    }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapSpace(res) };
    });
  };

  Backend.toggleLab = function ({ id, UID, disable }) {
    return this.getLabById({ id, UID }).then((res) => {
      if (res.error) return res;
      return this.apiCallV2(`/spaces/${id}`, 'PATCH', { deleted: disable }).then((r) => {
        if (r.status !== 200) return r;
        return { status: r.status, ...mapSpace(r) };
      });
    });
  };

  Backend.getMachineByLab = function (UID, id) {
    return allCatalogPages(this, `/spaces/${id}/tool_instances`).then((res) => mapList(res, mapToolInstance));
  };

  Backend.getPermissionsByLab = function (UID, id) {
    return this.apiCallV2(`/spaces/${id}/permissions`, 'GET').then((res) =>
      mapList(res, mapSpacePerm)
    );
  };

  Backend.getUsersByLab = function (UID, id) {
    // Admins only — every swipe-in creates a lab_permissions row for visit
    // counting, so an unfiltered list is mostly regular visitors.
    return this.apiCallV2(`/spaces/${id}/permissions`, 'GET', null, {
      per_page: 200,
      is_admin: true,
    }).then((res) => mapList(res, mapSpacePerm));
  };

  // V1 returned every request in the lab unpaginated ("Returns all requests
  // in a lab"); V2's /spaces/{id}/reservations defaults to per_page=20 (max
  // 200). Callers here (e.g. TopUsers' lifetime request counts) need the
  // full history, so walk pages until they're all collected.
  Backend.getRequestsByLab = function (UID, Lab, page = 1, per_page = 200, accumulated = []) {
    return this.apiCallV2(`/spaces/${Lab}/reservations`, 'GET', null, { page, per_page }).then((res) => {
      if (res.status !== 200) {
        return res;
      }
      const data = accumulated.concat(res.data || []);
      const gotFullPage = (res.data || []).length === per_page;
      if (gotFullPage && data.length < (res.total || 0)) {
        return this.getRequestsByLab(UID, Lab, page + 1, per_page, data);
      }
      return { ...res, data };
    });
  };

  Backend.getActiveUsersByLab = function (id, num) {
    return this.apiCallV2(`/spaces/${id}/active_users`, 'GET', null, {
      per_page: num || 100,
    }).then((res) =>
      mapList(res, (u) => ({
        ...mapUser(u),
        swipe_in_time: u.swipe_in_time,
        reason: u.reason,
      }))
    );
  };

  Backend.newLabPermission = function (UID, user, lab) {
    return this.apiCallV2('/space_permissions/', 'POST', {
      user_id: user,
      space_id: lab,
      is_admin: true,
    }).then((res) => {
      if (res.status !== 200 && res.status !== 201) return res;
      return { status: res.status, ...mapSpacePerm(res) };
    });
  };

  Backend.deleteLabPermission = function (UID, id) {
    return this.apiCallV2(`/space_permissions/${id}`, 'DELETE');
  };

  Backend.setLabPermissionAdmin = function (UID, id, isAdmin) {
    return this.apiCallV2(`/space_permissions/${id}`, 'PATCH', {
      is_admin: isAdmin,
    }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapSpacePerm(res) };
    });
  };

  // ── Users ─────────────────────────────────────────────────────────────────

  Backend.getUsers = function (UID, num) {
    return this.apiCallV2('/users/', 'GET', null, { per_page: num || 20 }).then((res) =>
      mapList(res, mapUser)
    );
  };

  Backend.getUser = function (UID) {
    return this.apiCallV2(`/users/${UID}`, 'GET').then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapUser(res) };
    });
  };

  Backend.convertSwipeNumToUID = function (swipeNum) {
    return this.apiCallV2('/users/lookup', 'GET', null, { swipe_number: swipeNum }).then(
      (res) => {
        if (res.status !== 200) return res;
        return { status: res.status, UID: res.uid ?? res.id, ...mapUser(res) };
      }
    );
  };

  Backend.getUserInfoFromUID = function (UID) {
    return this.apiCallV2('/users/lookup', 'GET', null, { uid: UID }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapUser(res) };
    });
  };

  Backend.newUser = function ({ UID, args }) {
    delete args['status'];
    return this.apiCallV2(`/users/${UID}`, 'PUT', {
      swipe_number: args.swipeNum,
      directory_id: args.directoryID,
      first_name: args.firstName,
      last_name: args.lastName,
      email: args.email,
      is_super_admin: args.isSuperAdmin || false,
    }).then((res) => {
      if (res.status !== 200 && res.status !== 201) return res;
      return { status: res.status, ...mapUser(res) };
    });
  };

  Backend.editUser = function ({ UID, args }) {
    delete args['status'];
    const body = {};
    if (args.swipeNum !== undefined) body.swipe_number = args.swipeNum;
    if (args.directoryID !== undefined) body.directory_id = args.directoryID;
    if (args.firstName !== undefined) body.first_name = args.firstName;
    if (args.lastName !== undefined) body.last_name = args.lastName;
    if (args.email !== undefined) body.email = args.email;
    if (args.isSuperAdmin !== undefined) body.is_super_admin = args.isSuperAdmin;
    return this.apiCallV2(`/users/${UID}`, 'PATCH', body).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapUser(res) };
    });
  };

  Backend.getLabPermissionsByUser = function (UID) {
    return this.apiCallV2(`/users/${UID}/space_permissions`, 'GET').then((res) =>
      mapList(res, mapSpacePerm)
    );
  };

  Backend.getRequestsByUser = function (UID) {
    return this.apiCallV2(`/users/${UID}/reservations`, 'GET').then((res) => {
      if (res.error) return res;
      return {
        status: res.status,
        items: res.data || [],
        page: res.page,
        per_page: res.per_page,
      };
    });
  };

  Backend.getMachineTypesByUser = function (UID) {
    return this.apiCallV2(`/users/${UID}/tool_permissions`, 'GET');
  };

  // ── QR kiosk login ────────────────────────────────────────────────────────

  // Mints a fresh single-use QR login token for UID (self or admin, session
  // auth only — see backend/api_v2/controllers/qr_login.py). Any previously
  // active token for this user is invalidated server-side.
  Backend.generateQrLoginToken = function (UID) {
    return this.apiCallV2(`/users/${UID}/qr_login_token`, 'POST').then((res) => {
      if (res.status !== 201) return res;
      return { status: res.status, token: res.token, expiresAt: res.expires_at };
    });
  };

  // Invalidates UID's active QR login token, if any (e.g. user navigates
  // away or clicks Cancel before it's scanned).
  Backend.cancelQrLoginToken = function (UID) {
    return this.apiCallV2(`/users/${UID}/qr_login_token`, 'DELETE');
  };

  // Kiosk-side redemption: exchanges a single-use token (scanned as keyboard
  // input, same path as a card swipe) for the minting user's identity. The
  // token is consumed on first successful redemption regardless of what the
  // kiosk does next. Returns the same user shape convertSwipeNumToUID/getUser
  // do, so KioskLogin can feed it into the existing session-creation flow.
  Backend.redeemQrLoginToken = function (token, spaceId) {
    return this.apiCallV2('/kiosk/qr_login', 'POST', {
      token,
      space_id: spaceId,
    }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapUser(res), registered: res.registered };
    });
  };

  Backend.checkBadgrAuthentication = function (UID) {
    return this.apiCallV2(`/users/${UID}/auth_check`, 'GET').then((res) => {
      const providers = res.providers || {};
      const authenticated = Object.values(providers).some((p) => p.linked);
      return { authenticated, providers, v1Linked: res.v1_linked || false };
    });
  };

  Backend.getActiveMachinesByUserForLab = function (UID, lab) {
    return this.apiCallV2(`/users/${UID}/active_tool_instances`, 'GET', null, {
      space_id: lab,
    }).then((res) => mapList(res, mapToolInstance));
  };

  // ── Sessions (Dashboard) ───────────────────────────────────────────────────

  Backend.getUserSessions = function (UID, { status, spaceId, perPage } = {}) {
    return this.apiCallV2(`/users/${UID}/sessions`, 'GET', null, {
      status,
      space_id: spaceId,
      per_page: perPage,
    }).then((res) => mapList(res, mapSession));
  };

  Backend.endSession = function (sessionId) {
    return this.apiCallV2(`/sessions/${encodeURIComponent(sessionId)}/end`, 'PATCH').then((res) => {
      if (res.status !== 200) return res;
      // apiCallV2 overwrites any body `status` with the HTTP status (client-wide
      // convention: res.status === HTTP status), so the session's lifecycle status
      // isn't recoverable here — drop it rather than expose the HTTP code as a fake
      // domain status. Callers needing lifecycle state refetch via getUserSessions.
      const { status: _httpStatus, ...session } = mapSession(res);
      return { status: res.status, ...session };
    });
  };

  Backend.getSpacesStatus = function () {
    return this.apiCallV2('/spaces/status', 'GET').then((res) =>
      mapList(res, (s) => ({ id: s.id, name: s.name, activeCount: s.active_count }))
    );
  };

  Backend.getSpaceToolInstanceStatus = function (spaceId) {
    return this.apiCallV2(`/spaces/${spaceId}/tool_instances/status`, 'GET').then((res) =>
      mapList(res, (t) => ({
        id: t.id,
        name: t.name,
        status: t.status,
        isInUse: t.is_in_use,
        availabilityMode: t.availability_mode,
      }))
    );
  };

  // Every page: callers look tools up by ID, and the API defaults to 20 per page.
  Backend.getTools = function () {
    return allCatalogPages(this, '/tools/').then((res) => mapList(res, mapTool));
  };

  // ── Tool Instances (Machines) ─────────────────────────────────────────────

  Backend.getMachineById = function (UID, id) {
    return this.apiCallV2(`/tool_instances/${id}`, 'GET').then((res) => {
      if (res.status !== 200) return res;
      const mapped = mapToolInstance(res);
      // Enrich machineType with string name so EditMachineDropDown can match it.
      return this.apiCallV2(`/tools/${res.tool_id}`, 'GET')
        .then((toolRes) => {
          if (toolRes.status === 200) {
            mapped.machineType = toolRes.name;
            mapped.machineTypeName = toolRes.name;
          }
          return { status: res.status, ...mapped };
        })
        .catch(() => ({ status: res.status, ...mapped }));
    });
  };

  Backend.newMachine = function ({ UID, name, machineType, lab, categoryID }) {
    return _resolveToolId(this, machineType).then((tool_id) => {
      if (tool_id === null) return _toolNotFound(machineType);
      return this.apiCallV2('/tool_instances/', 'POST', {
        name,
        tool_id,
        space_id: lab,
        category_id: categoryID ? parseInt(categoryID) : null,
      }).then((res) => {
        if (res.status !== 200 && res.status !== 201) return res;
        return { status: res.status, ...mapToolInstance(res) };
      });
    });
  };

  Backend.editMachine = function (UID, id, args) {
    const body = {};
    if (args.machineName !== undefined || args.name !== undefined)
      body.name = args.machineName ?? args.name;
    if (args.categoryID !== undefined) body.category_id = parseInt(args.categoryID);
    if (args.lab !== undefined) body.space_id = args.lab;
    if (args.deleted !== undefined) body.deleted = args.deleted;
    if (args.status !== undefined) body.status = args.status;

    const buildAndPatch = (tool_id) => {
      if (tool_id !== undefined) body.tool_id = tool_id;
      return this.apiCallV2(`/tool_instances/${id}`, 'PATCH', body).then((res) => {
        if (res.status !== 200) return res;
        return { status: res.status, ...mapToolInstance(res) };
      });
    };

    if (args.machineType !== undefined) {
      return _resolveToolId(this, args.machineType).then((tool_id) => {
        if (tool_id === null) return _toolNotFound(args.machineType);
        return buildAndPatch(tool_id);
      });
    }
    return buildAndPatch(undefined);
  };

  // ── Tools (Machine Types) ─────────────────────────────────────────────────

  Backend.getMachineTypes = function (UID, num, includeDeleted = false) {
    return allCatalogPages(this, '/tools/', {
      per_page: num || 100,
      include_deleted: includeDeleted || undefined,
    }).then((res) => mapList(res, mapTool));
  };

  Backend.newMachineType = function (UID, machine_TypeID, machineType) {
    return this.apiCallV2('/tools/', 'POST', { name: machineType }).then((res) => {
      if (res.status !== 200 && res.status !== 201) return res;
      return { status: res.status, ...mapTool(res) };
    });
  };

  Backend.updateMachineType = function (UID, id, machineType) {
    return this.apiCallV2(`/tools/${id}`, 'PATCH', { name: machineType }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapTool(res) };
    });
  };

  Backend.toggleMachineTypeArchive = function (UID, id, machineType, isDeleted) {
    return this.apiCallV2(`/tools/${id}`, 'PATCH', { is_deleted: isDeleted }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapTool(res) };
    });
  };

  // ── Tool Categories ───────────────────────────────────────────────────────

  Backend.getToolCategoriesByLab = function (UID, lab, num, includeDeleted = false) {
    return allCatalogPages(this, '/tool_categories/', {
      space_id: lab,
      per_page: num || 100,
      include_deleted: includeDeleted || undefined,
    }).then((res) => mapList(res, mapToolCategory));
  };

  Backend.newToolCategory = function (UID, categoryName, lab) {
    return this.apiCallV2('/tool_categories/', 'POST', {
      name: categoryName,
      space_id: lab,
    }).then((res) => {
      if (res.status !== 200 && res.status !== 201) return res;
      return { status: res.status, ...mapToolCategory(res) };
    });
  };

  Backend.updateToolCategory = function (UID, categoryID, categoryName, lab) {
    return this.apiCallV2(`/tool_categories/${categoryID}`, 'PATCH', {
      name: categoryName,
      space_id: lab,
    }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapToolCategory(res) };
    });
  };

  Backend.toggleToolCategoryArchive = function (
    UID,
    categoryID,
    categoryName,
    labID,
    isDeleted
  ) {
    return this.apiCallV2(`/tool_categories/${categoryID}`, 'PATCH', {
      name: categoryName,
      space_id: labID,
      is_deleted: isDeleted,
    }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapToolCategory(res) };
    });
  };

  Backend.archiveToolCategory = function (UID, categoryID, lab) {
    return this.apiCallV2(`/tool_categories/${categoryID}`, 'PATCH', {
      is_deleted: true,
    }).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapToolCategory(res) };
    });
  };

  // ── Sessions (Swipes) ─────────────────────────────────────────────────────

  Backend.prepareCheckIn = function ({ UID, userSwipeNum, lab, reason, intervalMinutes, reasonId }) {
    return this.apiCallV2('/sessions/draft', 'POST', {
      user_id: UID, swipe_number: userSwipeNum, space_id: lab,
      reason, interval_minutes: intervalMinutes,
      ...(reasonId != null ? { reason_id: reasonId } : {}),
    });
  };

  Backend.completeCheckIn = function ({ draftToken, machineIds }) {
    return this.apiCallV2('/sessions/complete', 'POST', {
      draft_token: draftToken, tool_instance_ids: [...new Set(machineIds)].sort((a, b) => a - b),
    });
  };

  Backend.userSwipeIn = function ({ userUID, userSwipeNum, lab }) {
    return this.apiCallV2('/sessions/', 'POST', {
      user_id: userUID,
      swipe_number: userSwipeNum,
      space_id: lab,
    });
  };

  Backend.userSwipeOut = function (id, lab) {
    return this.withActiveSession('checkout', id, lab, sessionId =>
      this.apiCallV2(`/sessions/${encodeURIComponent(sessionId)}/end`, 'PATCH'));
  };

  Backend.userAutoSwipeOut = function (id, lab, interval, reason) {
    return this.withActiveSession(['estimate', interval, reason], id, lab, sessionId => {
      const body = { interval_minutes: interval };
      if (reason) body.reason = reason;
      return this.apiCallV2(`/sessions/${encodeURIComponent(sessionId)}/auto_end`, 'POST', body);
    });
  };

  Backend.isUserSwipedIn = async function (id, lab) {
    const res = await this.apiCallV2('/sessions/active', 'GET', null, {
      user_id: id,
      space_id: lab,
    });
    if (res.error) return false;
    return Array.isArray(res.data) && res.data.length > 0;
  };

  Backend.getUserSwipeInTime = async function (id, lab) {
    const res = await this.apiCallV2('/sessions/active', 'GET', null, {
      user_id: id,
      space_id: lab,
    });
    if (res.error || !res.data || res.data.length === 0) return new Date();
    return new Date(res.data[0].started_at);
  };

  // Lab-agnostic self-service variants (v2-only, no v1 counterpart): let a user
  // check/end their own active session from a context (e.g. Profile page) that
  // doesn't know which lab they're currently in.
  Backend.isUserSwipedInAnywhere = async function (id) {
    const res = await this.apiCallV2('/sessions/active', 'GET', null, { user_id: id });
    if (res.error) return false;
    return Array.isArray(res.data) && res.data.length > 0;
  };

  Backend.selfSwipeOut = function (id) {
    return this.withActiveSession('self-checkout', id, undefined, sessionId =>
      this.apiCallV2(`/sessions/${encodeURIComponent(sessionId)}/end`, 'PATCH'));
  };

  // Returns the user's consecutive auto-swipeout streak count if the reminder
  // popup should be shown, or 0 if no popup is needed (mirrors the v1 contract).
  Backend.isLastSwipeOutAuto = async function (id) {
    // If there's any active session for this user, no reminder.
    const activeRes = await this.apiCallV2('/sessions/active', 'GET', null, {
      user_id: id,
    });
    if (!activeRes.error && activeRes.data && activeRes.data.length > 0) return 0;

    // Most recent ended session (the list is ordered newest-first).
    const res = await this.apiCallV2('/sessions/', 'GET', null, {
      user_id: id,
      per_page: 1,
      status: 'ended',
    });
    if (res.error || !res.data || res.data.length === 0) return 0;
    // auto_ended is true only when the system ended the session, not when the
    // user signed out manually before a scheduled auto-end.
    if (!res.data[0].auto_ended) return 0;

    // Fetch the user's streak counter for the tiered message.
    const userRes = await this.apiCallV2(`/users/${id}`, 'GET');
    if (userRes.error || userRes.status !== 200) return 1;
    return Math.max(1, userRes.consecutive_no_swipeout || 1);
  };

  // ── Reservations (Requests) ───────────────────────────────────────────────

  Backend.newRequest = function ({ UID, machineIds, reason, lab, sessionId }) {
    const toolIds = [...new Set(machineIds)].sort((a, b) => a - b);
    const send = id => this.apiCallV2('/reservations/', 'POST', {
      user_id: UID,
      tool_instance_ids: toolIds,
      reason,
      space_id: lab,
      session_id: id,
    });
    if (sessionId) return send(sessionId);
    return this.withActiveSession(['tool-usage', toolIds, reason], UID, lab, send);
  };

  Backend.downloadAllRequests = function (UID) {
    return this.apiCallV2('/reservations/', 'GET', null, {}, 'text/csv');
  };

  Backend.downloadLabRequests = function (UID, labID) {
    return this.apiCallV2(`/spaces/${labID}/reservations`, 'GET', null, {}, 'text/csv');
  };

  Backend.downloadMachineRequests = function (UID, machineID) {
    return this.apiCallV2(
      `/tool_instances/${machineID}/reservations`,
      'GET',
      null,
      {},
      'text/csv'
    );
  };

  // ── Custom Reasons ────────────────────────────────────────────────────────

  Backend.getReasons = function (UID, lab, includeDeleted = false) {
    return allCatalogPages(this, `/spaces/${lab}/custom_reasons`, {
      per_page: 200,
      include_deleted: includeDeleted || undefined,
    }).then((res) => {
      const mapped = mapList(res, mapCustomReason);
      if (mapped.items) _cacheReasons(lab, mapped.items);
      return mapped;
    });
  };

  Backend.getAllReasons = function (UID, lab) {
    return allCatalogPages(this, `/spaces/${lab}/custom_reasons`, {
      per_page: 100,
    }).then((res) => {
      const mapped = mapList(res, mapCustomReason);
      if (mapped.items) _cacheReasons(lab, mapped.items);
      return mapped;
    });
  };

  Backend.newReason = function (UID, custom_reason, time_interval, labID,
                                enable_email_reminder, enable_time_tracking) {
    return this.apiCallV2(`/spaces/${labID}/custom_reasons`, 'POST', {
      reason: custom_reason,
      time_interval,
      ...(enable_email_reminder !== undefined ? { enable_email_reminder } : {}),
      ...(enable_time_tracking !== undefined ? { enable_time_tracking } : {}),
    }).then((res) => {
      if (res.status !== 200 && res.status !== 201) return res;
      if (res.id) Backend._reasonSpaceCache[res.id] = labID;
      return { status: res.status, ...mapCustomReason(res) };
    });
  };

  Backend.getReason = function (reason_id) {
    const spaceId = this._reasonSpaceCache[reason_id];
    if (!spaceId) {
      return Promise.resolve({
        status: 404,
        error: 'Space not cached — call getReasons first',
      });
    }
    return this.apiCallV2(`/spaces/${spaceId}/custom_reasons/${reason_id}`, 'GET').then(
      (res) => {
        if (res.status !== 200) return res;
        return { status: res.status, ...mapCustomReason(res) };
      }
    );
  };

  Backend.editReason = function (UID, reason_id, custom_reason, time_interval,
                                 enable_email_reminder, enable_time_tracking) {
    const spaceId = this._reasonSpaceCache[reason_id];
    if (!spaceId) {
      return Promise.resolve({
        status: 404,
        error: 'Space not cached — call getReasons first',
      });
    }
    return this.apiCallV2(
      `/spaces/${spaceId}/custom_reasons/${reason_id}`,
      'PATCH',
      {
        reason: custom_reason,
        time_interval,
        ...(enable_email_reminder !== undefined ? { enable_email_reminder } : {}),
        ...(enable_time_tracking !== undefined ? { enable_time_tracking } : {}),
      }
    ).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapCustomReason(res) };
    });
  };

  Backend.toggleCustomReasonArchive = function (
    UID,
    reason_id,
    custom_reason,
    time_interval,
    isDeleted
  ) {
    const spaceId = this._reasonSpaceCache[reason_id];
    if (!spaceId) {
      return Promise.resolve({
        status: 404,
        error: 'Space not cached — call getReasons first',
      });
    }
    return this.apiCallV2(
      `/spaces/${spaceId}/custom_reasons/${reason_id}`,
      'PATCH',
      { reason: custom_reason, time_interval, is_deleted: isDeleted }
    ).then((res) => {
      if (res.status !== 200) return res;
      return { status: res.status, ...mapCustomReason(res) };
    });
  };

  // ── Badges ────────────────────────────────────────────────────────────────

  Backend.getBadgeRequirementChoices = async function () {
    const providers = await this.apiCallV2('/badges/providers', 'GET');
    if (providers.status !== 200 || providers.error || !Array.isArray(providers.data)) return providers;
    const credentials = [];
    for (const provider of providers.data) {
      let cursor;
      const seen = new Set();
      do {
        const response = await this.apiCallV2('/badges/classes', 'GET', null,
          { provider: provider.name, per_page: 200, cursor });
        if (response.status !== 200 || response.error || !Array.isArray(response.data)) return response;
        credentials.push(...response.data);
        cursor = response.next_cursor;
        if (cursor && seen.has(cursor)) return { status: 502, error: 'Credential catalog pagination failed' };
        if (cursor) seen.add(cursor);
      } while (cursor);
    }
    const tools = await allCatalogPages(this, '/tools/');
    if (tools.status !== 200 || tools.error) return tools;
    return { status: 200, credentials: [...new Map(credentials.map(item =>
      [`${item.provider}:${item.id}`, item])).values()], tools: tools.data.filter(tool => !tool.is_deleted) };
  };

  Backend.createBadgeRequirement = function (UID, credential, toolId, spaceId) {
    return this.apiCallV2('/badges/', 'POST', {
      badge_class_entity_id: credential.id, provider: credential.provider,
      tool_id: toolId, space_id: spaceId,
    });
  };

  Backend.getFullBadgeInfo = async function (UID) {
    const badges = await allCatalogPages(this, '/badges/');
    if (badges.status !== 200 || badges.error) return badges;
    const successfulBadges = [];
    const failedBadgeIds = [];
    for (const badge of badges.data || []) {
      try {
        const info = await this.apiCallV2(
          `/badges/classes/${badge.badge_class_entity_id}`,
          'GET',
          null,
          { user_id: UID, provider: badge.provider || 'parchment' }
        );
        if (info.status === 200) {
          successfulBadges.push({
            ...badge,
            badgeclass_entity_id: badge.badge_class_entity_id,
            badge_name: info.name,
            description: info.description,
            creator: info.createdBy,
          });
        } else {
          failedBadgeIds.push(badge.badge_class_entity_id);
        }
      } catch {
        failedBadgeIds.push(badge.badge_class_entity_id);
      }
    }
    const result = { status: badges.status, items: successfulBadges };
    // Only set `alert` when something failed: like V1, the key's presence
    // is the failure signal.
    if (failedBadgeIds.length > 0) {
      result.alert = `Some badges could not be loaded. Verify badge IDs: ${failedBadgeIds.join(', ')}`;
    }
    return result;
  };

  Backend.newBadgeMachineTypeRelationship = async function (
    UID,
    newEntityId,
    newMachineType,
    lab
  ) {
    const info = await this.apiCallV2(
      `/badges/classes/${newEntityId}`,
      'GET',
      null,
      { user_id: UID }
    );
    if (info.status !== 200) return { status: 400, error: 'Not an existing Badge ID' };
    const tool_id = await _resolveToolId(this, newMachineType);
    if (tool_id === null) return _toolNotFound(newMachineType);
    return this.apiCallV2('/badges/', 'POST', {
      badge_class_entity_id: newEntityId,
      tool_id,
      space_id: lab,
    });
  };

  Backend.updateBadgeMachineTypeRelationship = async function (
    UID,
    oldEntityId,
    updatedEntityId,
    oldMachineType,
    updatedMachineType,
    lab
  ) {
    const info = await this.apiCallV2(
      `/badges/classes/${updatedEntityId}`,
      'GET',
      null,
      { user_id: UID }
    );
    if (info.status !== 200) return { status: 400, error: 'Not an existing Badge ID' };
    const badgesRes = await this.apiCallV2('/badges/', 'GET');
    if (badgesRes.error) return badgesRes;
    const oldBadge = (badgesRes.data || []).find(
      (b) => b.badge_class_entity_id === oldEntityId
    );
    if (!oldBadge) return { status: 404, error: 'Badge not found' };
    const tool_id = await _resolveToolId(this, updatedMachineType);
    if (tool_id === null) return _toolNotFound(updatedMachineType);
    return this.apiCallV2(`/badges/${oldBadge.id}`, 'PATCH', {
      badge_class_entity_id: updatedEntityId,
      tool_id,
    });
  };

  Backend.deleteBadgeMachineTypeRelationship = async function (
    UID,
    entityId,
    machineType,
    lab
  ) {
    const badgesRes = await this.apiCallV2('/badges/', 'GET');
    if (badgesRes.error) return badgesRes;
    const badge = (badgesRes.data || []).find(
      (b) => b.badge_class_entity_id === entityId
    );
    if (!badge) return { status: 404, error: 'Badge not found' };
    return this.apiCallV2(`/badges/${badge.id}`, 'DELETE');
  };

  // ── Admin ─────────────────────────────────────────────────────────────────
  // V2 admin paths differ from v1 (renamed, restructured, query param changes).

  Backend.adminSearchUsers = function (UID, query, searchType = 'all', page = 1, perPage = 20, lab = null) {
    return this.apiCallV2('/admin/users/', 'GET', null, {
      q: query,
      type: searchType,
      page,
      per_page: perPage,
    }).then((res) => {
      if (res.error) return res;
      return {
        status: res.status,
        items: (res.data || []).map(mapUser),
        page: res.page,
        per_page: res.per_page,
        total: res.total,
        pages: Math.ceil((res.total || 0) / (res.per_page || perPage)) || 1,
      };
    });
  };

  Backend.adminGetUserDetails = function (UID, targetUID, lab = null) {
    return this.apiCallV2(`/admin/users/${targetUID}`, 'GET').then((res) => {
      if (res.status !== 200) return res;
      return {
        status: res.status,
        ...mapUser(res),
        hasBadgrAuth: res.has_badge_auth,
        hasBadgrAuthV1: res.has_badge_auth_v1,
        hasBadgrAuthV2: res.has_badge_auth_v2,
        badgeIdentityCount: res.badge_identity_count ?? 0,
        // Non-@umd.edu emails arrive already masked by the backend.
        badgeIdentities: (res.badge_identities || []).map((i) => ({
          id: i.id,
          provider: i.provider,
          email: i.email,
          emailMasked: !!i.email_masked,
          source: i.source,
          linkedAt: i.linked_at,
        })),
      };
    });
  };

  Backend.adminGetUserMachineAccess = function (UID, targetUID, lab) {
    return this.apiCallV2(`/admin/users/${targetUID}/tool_access`, 'GET', null, {
      space_id: lab,
    }).then((res) => {
      if (res.error || res.status !== 200) return res;
      return {
        status: res.status,
        visit_count: res.visit_count ?? 0,
        items: (res.data || []).map((entry) => ({
          machineID: entry.tool_instance?.id,
          machineName: entry.tool_instance?.name,
          machineType: entry.tool_instance?.tool_id,
          machineTypeName: null,
          lab: entry.tool_instance?.space_id,
          canAccess: entry.can_access,
          reasons: (entry.reasons || []).map((r) => ({
            type: r.type,
            message: r.message === 'Tool is out of service' ? 'Tool instance is out of service' : apiDisplayMessages.get(r.message) || r.message,
            requiredBadgeEntityIds: r.required_badge_entity_ids || [],
          })),
        })),
      };
    });
  };

  Backend.adminGetUserSwipeHistory = function (UID, targetUID, page = 1, perPage = 20, lab = null) {
    return this.apiCallV2(`/admin/users/${targetUID}/sessions`, 'GET', null, {
      page,
      per_page: perPage,
    }).then((res) => {
      if (res.error) return res;
      return {
        status: res.status,
        items: res.data || [],
        page: res.page,
        per_page: res.per_page,
        total: res.total,
        pages: Math.ceil((res.total || 0) / (res.per_page || perPage)) || 1,
      };
    });
  };

  Backend.adminResetUserBadges = function (UID, targetUID) {
    return this.apiCallV2(`/admin/users/${targetUID}/badge_reset`, 'POST');
  };


  Backend.adminGetUserUnknownBadges = function (UID, targetUID) {
    return this.apiCallV2(`/admin/users/${targetUID}/unmatched_badges`, 'GET').then((res) => {
      if (res.error) return res;
      return { ...res, items: res.data || [] };
    });
  };

  Backend.adminGetAuditLog = function (UID, filters = {}, page = 1, perPage = 50) {
    return this.apiCallV2('/admin/audit_log', 'GET', null, {
      ...filters,
      page,
      per_page: perPage,
    });
  };

  // The recipient is always the calling admin's own registered email (the
  // backend never accepts a client-supplied address -- see admin.py) --
  // there's no `email` param here anymore.
  Backend.adminSendTestEmail = function (UID, tier = 1) {
    return this.apiCallV2('/admin/debug/test_email', 'POST', { tier });
  };

  Backend.adminGetEmailStatus = function (UID) {
    return this.apiCallV2('/admin/debug/email_status', 'GET');
  };

  Backend.adminGetActiveSessions = function (UID, lab = null) {
    if (!lab) return Promise.resolve({ status: 400, error: 'space_id required for v2 active sessions' });
    return this.adminGetActiveSessionsV2(lab);
  };

  // Replaces the manual v2 URL construction from apiHook with apiCallV2.
  Backend.adminGetActiveSessionsV2 = function (spaceId) {
    return this.apiCallV2(`/admin/spaces/${spaceId}/active_sessions`, 'GET');
  };

  Backend.adminForceLogout = function (UID, targetUID, lab = null) {
    return this.apiCallV2(`/admin/users/${targetUID}/force_logout`, 'POST', null, {
      space_id: lab || undefined,
    });
  };

  Backend.adminForceLogoutAll = function (UID, lab = null) {
    return this.apiCallV2('/admin/force_logout', 'POST', null, {
      space_id: lab || undefined,
    });
  };

  Backend.adminGetLabSwipeLog = function (UID, labId, startDate = null, endDate = null, page = 1, perPage = 50) {
    return this.apiCallV2(`/admin/spaces/${labId}/session_log`, 'GET', null, {
      start_date: startDate,
      end_date: endDate,
      page,
      per_page: perPage,
    });
  };

  Backend.deleteBadgeRequirement = function (UID, requirementId) {
    return this.apiCallV2(`/badges/${encodeURIComponent(requirementId)}`, 'DELETE');
  };

  Backend.adminExportLabSwipeLog = function (labId, startDate = null, endDate = null) {
    return this.apiCallV2(`/admin/spaces/${labId}/session_log`, 'GET', null, {
      start_date: startDate,
      end_date: endDate,
    }, 'text/csv');
  };

  Backend.adminGetLabStatistics = function (UID, labId, period = 'week', startDate = null, endDate = null) {
    return this.apiCallV2(`/admin/spaces/${labId}/statistics`, 'GET', null, {
      period,
      start_date: startDate,
      end_date: endDate,
    });
  };

  Backend.adminGetFailedSwipes = function (UID, labId, page = 1, perPage = 50, stage = null) {
    return this.apiCallV2(`/spaces/${labId}/failed_swipes`, 'GET', null, {
      page,
      per_page: perPage,
      stage,
    });
  };

  Backend.adminReportFailedSwipe = function (labId, data) {
    return this.apiCallV2(`/spaces/${labId}/failed_swipes`, 'POST', data);
  };

  Backend.adminClearFailedSwipes = function (UID, labId) {
    return this.apiCallV2(`/spaces/${labId}/failed_swipes`, 'DELETE');
  };

} // end V2 implementations

// Expose the Backend object to other files
export default Backend;
